/**
 * Portfolio subtree traversal — the RELIABLE hierarchy walk, by parentId.
 *
 * The Portfolio model carries its hierarchy two ways: `parentId` (the back-link —
 * one source of truth, mirrored from `portfolios.parent_id`) and `childIds` (a
 * denormalized forward-link, `child_ids`). These DRIFT: a re-parent that sets a
 * child's parentId without also rewriting the parent's child_ids array leaves
 * child_ids stale. That is exactly what the Gentex value-stream restructure did —
 * projects were moved onto value-stream sub-portfolios (their parentId set) but the
 * Gentex Corporation root's child_ids stayed empty. Any traversal that trusted
 * childIds (e.g. the old usePortfolioScope) then collected only the root, so the
 * parent-portfolio dashboard rolled up ZERO projects.
 *
 * Traversing by parentId is immune to that drift, so it's the canonical walk.
 */
export interface PortfolioNode {
  id: string
  parentId?: string | null
}

/**
 * The `rootId` portfolio plus every descendant beneath it (by parentId), as a Set
 * of ids. Empty set when `rootId` is null/undefined. Cycle-safe.
 */
export function collectPortfolioSubtreeIds(
  portfolios: ReadonlyArray<PortfolioNode>,
  rootId: string | null | undefined,
): Set<string> {
  const ids = new Set<string>()
  if (!rootId) return ids

  const childrenByParent = new Map<string, string[]>()
  for (const p of portfolios) {
    const pid = p.parentId ?? null
    if (!pid) continue
    const arr = childrenByParent.get(pid)
    if (arr) arr.push(p.id)
    else childrenByParent.set(pid, [p.id])
  }

  const queue: string[] = [rootId]
  while (queue.length > 0) {
    const id = queue.shift()!
    if (ids.has(id)) continue // guards against cycles + repeated ids
    ids.add(id)
    const kids = childrenByParent.get(id)
    if (kids) queue.push(...kids)
  }
  return ids
}
